/**
 * combatStep.ts
 * Enemy update and combat resolution for one fixed physics step.
 *
 * Extracted from physicsStep.ts to keep both files within the 300-line limit.
 * Called once per tick by stepPhysics(), after Nova physics is resolved.
 *
 * Handles:
 *   - Enemy AI updates (update() on each active enemy)
 *   - Stomp detection (Nova falling feet-first onto an enemy)
 *   - Projectile–enemy collision
 *   - Enemy–Nova body contact (damage Nova)
 *   - Spike-hazard–Nova contact, delegated to `hazardStep.ts`
 *
 * Architecture boundary: NO React, NO Reanimated, NO async.
 *
 * Phase 2 — Combat, Enemies, Checkpoints, HUD.
 *
 * @module combatStep
 */

import { gameState } from './gameState';
import { aabbOverlaps } from './collision';
import {
  NOVA_WIDTH,
  NOVA_HEIGHT,
  NOVA_COMBAT_TOP_INSET,
  NOVA_COMBAT_CROUCH_EXTRA_TOP_INSET,
  VIEWPORT_HEIGHT,
} from '../../constants/gameConstants';
import Logger from '../utils/logger';
import { emitBurst } from './particleSystem';
import { spawnHitEffect, stepHitEffects } from './hitEffectVfx';
import { STOMP_BOUNCE, MAX_FALL_SPEED, FIXED_STEP } from './physics';
import { applyNovaContactDamage } from './novaDamage';
import { stepHazards } from './hazardStep';
import { BOSS_ENTITY_TYPES } from './bossArena';
import type { Enemy, LevelRuntime, NovaRuntime } from './gameState';
import type { ObjectPool } from '../utils/objectPool';
import type { Projectile } from '../entities/Projectile';

// DECISION: Module-level reusable objects to avoid GC pressure in hot path.
// Updated at the start of stepCombat() each tick rather than allocating fresh.
// DECISION: Combat hitbox uses NOVA_COMBAT_TOP_INSET to shrink from the top so
// flying enemies don't trigger damage above Nova's visible head.
const _novaHitbox = { x: 0, y: 0, width: NOVA_WIDTH, height: NOVA_HEIGHT - NOVA_COMBAT_TOP_INSET };
const _enemyHitbox = { x: 0, y: 0, width: 0, height: 0 };
const _shardHitbox = { x: 0, y: 0, width: 0, height: 0 };
const _levelWrap: LevelRuntime = { platforms: [], oneWayPlatforms: [], width: 0, height: 0 };

/**
 * Advance and prune hit-effect timers so rendering stays read-only.
 * Called once per physics tick from stepPhysics.
 *
 * Thin pass-through to the pooled implementation in `hitEffectVfx.ts`;
 * kept here so `physicsStep.ts` doesn't have to re-import on top of an
 * already-large import block.
 *
 * @param dt - Fixed timestep in seconds.
 * @returns Nothing; pool slots are mutated in place.
 */
export function tickHitEffects(dt: number): void {
  stepHitEffects(dt);
}

/**
 * Vertical centre of an enemy's *combat* box — the box after `combatTopInset`
 * is applied, which is the one every hit test in this module uses.
 *
 * DECISION: derived from the enemy rather than read off `_enemyHitbox` so it is
 * safe at the pre-populate splice path (line ~115), where `_enemyHitbox` still
 * holds the previous iteration's enemy.
 *
 * @param enemy - Enemy whose combat-box centre is wanted.
 * @returns World-space Y of the combat box's vertical centre.
 */
function combatCenterY(enemy: Enemy): number {
  const topInset = enemy.combatTopInset ?? 0;
  return enemy.y + topInset + (enemy.height - topInset) / 2;
}

// DECISION: Centralised splice helper — stomp, projectile, and post-update
// splice paths all route through this so bossDefeated cannot be missed.
function spliceDeadEnemy(i: number, enemy: Enemy): void {
  // DECISION: burst on the combat box, not the raw box. `combatTopInset` exists
  // because the raw box carries empty air above the sprite (11px on Thornhop),
  // so the raw midpoint sits above the art the burst is meant to replace.
  emitBurst(enemy.x + enemy.width / 2, combatCenterY(enemy), enemy.color, 6);
  if (BOSS_ENTITY_TYPES.has(enemy.entityType)) gameState.bossDefeated = true;
  gameState.enemies.splice(i, 1);
}

/**
 * Run one combat tick: update all active enemies and resolve combat interactions.
 *
 * @param dt          - Fixed timestep in seconds.
 * @param nova        - Current Nova runtime state (mutated in-place on contact).
 * @param platforms   - Level platform list forwarded to enemy AI.
 * @param levelWidth  - Level width for enemy bounds.
 * @param levelHeight - Level height for enemy bounds.
 * @param pool        - Projectile pool so dead shards can be released.
 * @param wasFalling  - True if Nova was falling (vy > 0) before collision resolution.
 */
export function stepCombat(
  dt: number,
  nova: NovaRuntime,
  platforms: LevelRuntime['platforms'],
  levelWidth: number,
  levelHeight: number,
  pool: ObjectPool<Projectile>,
  wasFalling: boolean,
): void {
  // Update reusable hitbox/level objects instead of allocating new ones.
  // DECISION: When crouched, shift the combat hitbox top further down and
  // shrink its height to match the visible sprite. Without this, flying
  // enemies (e.g. Gloombat) hit the player above the crouched head.
  const isCrouched = nova.state === 'CROUCH';
  const extraTop = isCrouched ? NOVA_COMBAT_CROUCH_EXTRA_TOP_INSET : 0;
  _novaHitbox.x = nova.x;
  _novaHitbox.y = nova.y + NOVA_COMBAT_TOP_INSET + extraTop;
  _novaHitbox.height = NOVA_HEIGHT - NOVA_COMBAT_TOP_INSET - extraTop;
  _levelWrap.platforms = platforms;
  _levelWrap.width = levelWidth;
  _levelWrap.height = levelHeight;

  // DECISION: Track whether Nova already stomped this tick to prevent multi-stomp
  // on overlapping enemies. After the first stomp, vy becomes STOMP_BOUNCE (rising),
  // but wasFalling is still true for the rest of this tick.
  let didStompThisTick = false;

  for (let i = gameState.enemies.length - 1; i >= 0; i--) {
    const enemy = gameState.enemies[i];
    enemy.update(dt, _levelWrap, nova);

    if (enemy.dead) {
      spliceDeadEnemy(i, enemy);
      continue;
    }

    // DECISION: cull enemies that have fallen out of the world. Without this an
    // enemy lured into a pit falls at MAX_FALL_SPEED forever, still stepped and
    // collision-checked every frame — unkillable but nominally alive. Some
    // enemies reach a pit by design (BrambleRam's charge is committed so the
    // wall slam can stun it; Thornhop is a hopper), so a ledge probe cannot
    // cover them. This is the net that does.
    //
    // DECISION: does NOT route through spliceDeadEnemy — that emits a death
    // burst and sets bossDefeated. Falling out is not a defeat; crediting it
    // would let a player clear a boss by luring it into a pit.
    //
    // DECISION: margin matches Projectile's despawn rule, not Nova's bare
    // `y > levelHeight`. Nova dies at the floor because that IS the pit death;
    // an enemy at that depth may still be on screen, and popping it out from
    // under the player reads as a glitch.
    if (enemy.y > levelHeight + VIEWPORT_HEIGHT) {
      Logger.debug(
        'combatStep',
        `Culled ${enemy.entityType} below world at y=${Math.round(enemy.y)}`,
      );
      gameState.enemies.splice(i, 1);
      continue;
    }

    // DECISION: populate reusable hitbox instead of allocating via getHitbox().
    // Inset the top by the enemy's measured empty-air gap so contact damage,
    // shard hits, and the stomp band all agree with the visible sprite. Without
    // this the player is hit by air above an enemy's head — Thornhop's box
    // carried 11px (39%) of empty space, so most of its stomp band floated
    // above the art. Values are pinned to the sheets by enemyCombatInset.test.
    const topInset = enemy.combatTopInset ?? 0;
    _enemyHitbox.x = enemy.x;
    _enemyHitbox.y = enemy.y + topInset;
    _enemyHitbox.width = enemy.width;
    _enemyHitbox.height = enemy.height - topInset;

    // ── Stomp detection ────────────────────────────────────────────────────────
    // DECISION: Use wasFalling (captured before collision) rather than nova.vy,
    // because collision resolution may have already clamped vy to 0 or STOMP_BOUNCE.
    // Guard with !didStompThisTick to prevent multi-stomp on overlapping enemies.
    // Skip enemies already playing a death animation so Nova can't re-stomp a
    // corpse for a free bounce. Uses the `isDying` contract rather than a state
    // name, because each enemy names that state differently.
    // Nova falling (wasFalling), feet entering enemy top 75%, not already invincible.
    if (wasFalling && !didStompThisTick && !enemy.isDying && nova.invincibilityFrames === 0) {
      const novaFeetY = nova.y + NOVA_HEIGHT;
      // DECISION: Stomp zone is 75% of the (now inset) enemy height, floored at
      // one tick of terminal-velocity fall plus a margin. At MAX_FALL_SPEED
      // Nova covers ~15px per tick, so any band shorter than that can be
      // stepped straight over between samples — feet above the enemy on one
      // tick, past the band on the next, and a correctly-aimed dive charged as
      // contact damage instead. That was already reachable on the 20px GloomBat
      // (0.75 x 20 = 15px, exactly one tick) and insetting the box would have
      // made it reachable on every enemy. Derived from the physics constants
      // rather than hardcoded so retuning fall speed or the timestep keeps the
      // floor honest. Covered by combatStepStompBand.test.ts.
      const minBand = MAX_FALL_SPEED * FIXED_STEP + 2;
      // DECISION: clamp the band to the box. There is no separate vertical
      // overlap gate here, so a band taller than the body would register a
      // stomp with Nova's feet already below the enemy — a side collision that
      // must charge contact damage, not hand out a free bounce. The clamp only
      // binds on an enemy shorter than `minBand`; none ship, and
      // combatStepStompBand.test.ts asserts that, because such an enemy needs a
      // swept collision check rather than a wider band to stay un-straddleable.
      const stompBand = Math.min(
        _enemyHitbox.height,
        Math.max(_enemyHitbox.height * 0.75, minBand),
      );
      const stompContact =
        novaFeetY > _enemyHitbox.y &&
        novaFeetY < _enemyHitbox.y + stompBand &&
        nova.x + NOVA_WIDTH > _enemyHitbox.x &&
        nova.x < _enemyHitbox.x + _enemyHitbox.width;

      if (stompContact) {
        // DECISION: always bounce Nova — even on stomp-immune enemies like Thornhop.
        // Only apply damage to non-immune enemies.
        if (!enemy.stompImmune) {
          const killed = enemy.applyHit(true);
          if (killed) gameState.score += 100;
        }
        // DECISION: Grant brief invincibility on ALL stomp bounces (not just
        // stomp-immune). This prevents Nova from taking contact damage from
        // other overlapping enemies in the same tick after a successful stomp.
        // 15 frames (~0.25s) is enough to clear the enemy hitbox.
        nova.invincibilityFrames = Math.max(nova.invincibilityFrames, 15);
        nova.vy = STOMP_BOUNCE;
        nova.isGrounded = false;
        nova.isStomping = false;
        didStompThisTick = true;
        if (enemy.dead) spliceDeadEnemy(i, enemy);
        continue;
      }
    }

    // ── Projectile–enemy collision ─────────────────────────────────────────────
    // DECISION: Skip enemies playing their death animation, mirroring the stomp
    // guard above and the contact guard below. Without this, a shard that
    // arrives during the ~0.25s death window is swallowed by the corpse: it is
    // released back to the pool and spawns a hit spark, but `applyShardHit()`
    // returns 'IGNORED' so it deals no damage and scores nothing — and because
    // the shard is consumed it never reaches a live enemy standing behind the
    // dying one. `enemy.dead` is already handled at the top of the loop.
    if (!enemy.isDying) {
      for (let j = gameState.projectiles.length - 1; j >= 0; j--) {
        const shard = gameState.projectiles[j];
        // DECISION: populate reusable hitbox instead of allocating via getHitbox().
        _shardHitbox.x = shard.x;
        _shardHitbox.y = shard.y;
        _shardHitbox.width = shard.width;
        _shardHitbox.height = shard.height;
        if (aabbOverlaps(_shardHitbox, _enemyHitbox)) {
          const killed = enemy.applyHit();
          if (killed) gameState.score += 100;
          // DECISION: Spawn laser-hit VFX at the collision midpoint between
          // shard and enemy hitbox so the burst appears where contact happened.
          // Uses `_enemyHitbox` — the box the overlap above was actually tested
          // against — rather than `enemy.x/y`, which still includes the empty
          // air `combatTopInset` was introduced to exclude.
          spawnHitEffect(
            (shard.x + shard.width / 2 + _enemyHitbox.x + _enemyHitbox.width / 2) / 2,
            (shard.y + shard.height / 2 + _enemyHitbox.y + _enemyHitbox.height / 2) / 2,
          );
          pool.release(shard);
          gameState.projectiles.splice(j, 1);
          if (enemy.dead) spliceDeadEnemy(i, enemy);
          // DECISION: Always break after shard is consumed — one shard hits
          // one enemy. Without this break, the inner loop continues and could
          // fire applyHit() on the same enemy with a different shard.
          break;
        }
      }
    }

    // ── Nova–enemy body contact (damage Nova) ──────────────────────────────────
    // DECISION: Skip contact damage for any enemy playing its death animation —
    // a squished Nibbler or a Shellback mid-death is harmless. This reads the
    // `isDying` contract rather than matching 'SQUISHED', which only covered
    // Nibbler and let the five HURT-based enemies keep hurting Nova after she
    // had already killed them.
    if (enemy.dead || enemy.isDying) continue;
    if (aabbOverlaps(_novaHitbox, _enemyHitbox)) {
      applyNovaContactDamage(nova, _enemyHitbox.x + _enemyHitbox.width / 2);
    }
  }

  // ── Nova–spike contact (damage Nova) ─────────────────────────────────────────
  // DECISION: Resolved here rather than in physicsStep.ts, which is at the
  // 300-line cap. This is also the module that already owns "world contact
  // hurts Nova", so both damage sources share one i-frame guard per tick.
  stepHazards(nova, _novaHitbox);
}
